<template>
    <span
        v-if="controls && controls.length"
        class="media-controls"
        :class="[mode, position]"
    >
        <span
            class="media-controls-overlay"
            :class="[{ 'media-controls-overlay-transparent': overlayTransparent }]"
        />
        <span class="media-controls-main">
            <span
                v-for="(section, sectionIndex) in sections"
                :key="sectionIndex"
                class="media-controls-section"
            >
                <span
                    v-for="(control, controlIndex) in section"
                    :key="control.type + controlIndex"
                    class="media-controls-control-container"
                >
                    <Tooltip
                        :disabled="!control.tooltip"
                        position="top"
                        align="center"
                    >
                        <Component
                            :is="getControlComponent(control)"
                            v-bind="getControlBind(control)"
                            @click.stop.prevent="$emit('control', control.type)"
                        />
                        <template #tooltip>
                            <Txt size="xs">
                                {{ control.tooltip }}
                            </Txt>
                        </template>
                    </Tooltip>
                </span>
            </span>
        </span>
    </span>
</template>

<script>
import Txt from '@/components/form/txt';
import Button from '@/components/utility/button';
import Tooltip from '@/components/utility/tooltip';

export default {
    components: { Txt, Button, Tooltip },
    props: {
        mode: {
            type: String,
            default: 'overlay',
            validator: mode => ['overlay', 'block'].includes(mode)
        },
        controls: { type: Array, default: () => [] }, // { type string, isActive bool, component Component (optional, defaults to Button) }
        position: {
            type: String,
            default: 'bottom-center',
            validator: position => !position || ['center', 'bottom-center', 'bottom-left', 'bottom-right', 'top-right'].includes(position)
        },
        colour: { type: String, default: null },
        overlayTransparent: { type: Boolean, default: false }
    },
    emits: ['control'],
    computed: {
        sections() {
            let sections = [];
            let currentSection = [];
            this.controls.forEach(control => {
                if (control.type === 'divider') {
                    if (currentSection.length) sections.push(currentSection);
                    currentSection = [];
                    return;
                }
                currentSection.push(control);
            });
            sections.push(currentSection);
            return sections;
        }
    },
    methods: {
        getControlComponent(control) {
            return control.component || Button;
        },
        getControlBind({ type, isActive, colour }) {
            let bind = {
                variant: 'media',
                colour: 'translucent',
                icon: type,
                iconSize: '20',
                class: ['media-controls-button']
            };
            if (isActive) bind.class.push('active');
            if (type === 'fill') {
                bind.icon = 'frame-fill';
            }
            if (type === 'fit') {
                bind.icon = 'frame-fit';
            }
            if (type === 'background') {
                bind.icon = 'background-image';
            }
            if (type === 'edit') {
                bind.icon = 'edit';
            }
            if (type === 'image-edit') {
                bind.icon = 'image-edit';
            }
            if (type === 'delete') {
                bind.icon = 'trash';
                bind.hover = 'danger';
            }
            if (type === 'add-image') {
                bind.icon = 'add-image';
            }
            if (type === 'colour') {
                bind.icon = 'colour-fill';
                bind.style = { '--selected-colour': this.colour };
            }
            if (type === 'align-left') {
                bind.icon = 'image-align-left';
            }
            if (type === 'align-right') {
                bind.icon = 'image-align-right';
            }
            if (type === 'align-center') {
                bind.icon = 'image-align-center';
            }
            if (type === 'reposition') {
                bind.icon = 'reposition';
            }
            if (type === 'correct') {
                bind.icon = 'correct';
                bind.colour = colour ? colour : 'success';
            }
            if (type === 'close') {
                bind.icon = 'cross';
                bind.colour = colour ? colour : 'danger';
            }
            if (type === 'confirm:reposition') {
                bind.icon = 'correct';
                bind.colour = 'translucent';
                bind.hover = 'success';
            }
            if (type === 'cancel:reposition') {
                bind.icon = 'cross';
                bind.colour = 'translucent';
                bind.hover = 'danger';
            }
            if (type === 'caption') {
                bind.icon = 'caption';
            }
            return bind;
        }
    }
};
</script>

<style lang="less">

.media-controls {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    &.center {
        .media-controls-main {
            height: 48px;
            bottom: 16px;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }
    &.bottom-center {
        .media-controls-main {
            height: 48px;
            bottom: 16px;
            left: 50%;
            transform: translateX(-50%);
        }
    }
    &.bottom-left {
        .media-controls-main {
            height: 48px;
            bottom: 16px;
            left: 16px;
            transform: translateX(0);
        }
    }
    &.bottom-right {
        .media-controls-main {
            height: 48px;
            bottom: 16px;
            left: ~'calc(100% - 16px)';
            transform: translateX(-100%);
        }
    }
    &.top-right {
        .media-controls-main {
            height: 48px;
            top: 16px;
            left: ~'calc(100% - 16px)';
            transform: translateX(-100%);
        }
    }
    &-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: ~"rgba(var(--coal-rgb), 0.2)";
    }
    &-overlay-transparent {
        background-color: transparent;
        border-radius: 16px;
    }
    &-main {
        pointer-events: all;
        position: absolute;
        border-radius: 1000px;
        background-color: var(--night);
        display: flex;
        transition:
            left 0.15s var(--curve),
            top 0.15s var(--curve),
            transform 0.15s var(--curve);
    }
    &-section {
        display: flex;
        padding: 0 6px;
        align-items: center;
        &:not(:first-of-type) { border-left: 1px solid var(--coal); }
    }
    &-control-container {
        display: flex;
        padding: 0 2px;
    }
    &-button.cobutton.cobutton {
        padding: 6px;
    }
}
</style>
